// src/modules/units/tyres/views/yardscope.js
//
// The persistent header yard selector — the module's PRIMARY yard control.
//
// It holds no yard value of its own. It renders getSelectedYard() and, on change,
// writes back through setSelectedYard(), which announces SCOPE_CHANGED. It also
// subscribes to SCOPE_CHANGED, so a change made ANYWHERE else — the detail
// screen's yard dropdown in a later phase — re-syncs this control in the same
// tick. That is the "one store, two views" constraint honoured structurally:
// there is nothing to reconcile because there is only one value.
//
// "All yards" (the empty-string option value) is a first-class, default choice,
// not a blank waiting to be filled.
//
// Pure component: mount into a host element, get back { refresh, teardown }. No
// knowledge of the router or the header beyond the slot it is handed.

import { fetchYards } from './yards.api.js';
import { getSelectedYardId, setSelectedYard } from '../shared/tyre-scope.js';
import { onTyreEvent, TYRE_EVENTS } from '../shared/tyre-events.js';
import { esc } from '../shared/tyre-format.js';

const ALL = ''; // the All-yards <option> value

function optionsMarkup(yards, selectedId) {
  const all = `<option value="">All yards</option>`;
  const rows = yards.map((y) => {
    const id = y.Id;
    // With inclChild yards, two customers can each have a "Main Depot", so show
    // the owning customer to disambiguate. Own-customer yards still read plainly
    // enough; the label is never the IMEI.
    const label = y.customerName ? `${y.name} \u00b7 ${y.customerName}` : y.name;
    return `<option value="${esc(id)}"${id === selectedId ? ' selected' : ''}>${esc(label)}</option>`;
  }).join('');
  return all + rows;
}

/**
 * Mount the selector into `hostEl` (the header slot).
 * @returns {{ refresh: () => Promise<void>, teardown: () => void }}
 */
export function mountYardScope(hostEl) {
  hostEl.classList.add('tm-yardscope');
  hostEl.innerHTML = `
    <label class="tm-yardscope-label" for="tm-yard-select">Yard</label>
    <select class="tm-yardscope-select" id="tm-yard-select" data-tm-yard aria-label="Yard scope">
      <option value="">All yards</option>
    </select>`;

  const select = hostEl.querySelector('[data-tm-yard]');
  let yards = []; // cache so the change handler can resolve id -> yard object
  let refreshVersion = 0;
  let disposed = false;

  // User picks a yard -> write the single shared scope. All yards ('') clears it.
  select.addEventListener('change', () => {
    const id = select.value;
    const yard = id ? yards.find((y) => y.Id === id) || null : null;
    setSelectedYard(yard);
  });

  // A scope change from anywhere re-syncs the control. setSelectedYard no-ops on
  // an unchanged value, so writing the same id back here cannot loop.
  const offScope = onTyreEvent(TYRE_EVENTS.SCOPE_CHANGED, () => {
    select.value = getSelectedYardId() || ALL;
  });

  // A yard was created (or otherwise changed) elsewhere — rebuild the options so
  // the new one is immediately switchable from the header.
  const offYards = onTyreEvent(TYRE_EVENTS.YARDS_CHANGED, () => { refresh(); });

  async function refresh() {
    const version = ++refreshVersion;
    const res = await fetchYards();
    if (disposed || version !== refreshVersion || !res?.success) return;
    yards = res && res.success ? (res.data?.results || []) : [];

    const selectedId = getSelectedYardId();
    // If a persisted selection is no longer in the list (yard removed, or the
    // acting customer changed), fall back to All yards rather than showing a
    // phantom value. Clearing announces SCOPE_CHANGED, which repaints the option
    // list's `selected` and re-scopes every subscribing view.
    if (selectedId && !yards.some((y) => y.Id === selectedId)) {
      select.innerHTML = optionsMarkup(yards, null);
      setSelectedYard(null);
    } else {
      select.innerHTML = optionsMarkup(yards, selectedId);
      select.value = selectedId || ALL;
      if (selectedId) setSelectedYard(yards.find((y) => y.Id === selectedId));
    }
  }

  function teardown() {
    disposed = true;
    offScope();
    offYards();
  }

  return { refresh, teardown };
}
